import React, { useMemo, useState } from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";

// --- Tiny helpers -----------------------------------------------------------
const fmt = (n: number): string => new Intl.NumberFormat("th-TH", { maximumFractionDigits: 0 }).format(n);
const clamp = (n: number, lo: number, hi: number): number => Math.max(lo, Math.min(hi, n));

// --- Types ------------------------------------------------------------------
interface Recipe {
  pork_g: number;
  basil_g: number;
  chili_g: number;
  garlic_g: number;
  rice_g: number;
  oil_ml: number;
  sauces_thb: number;
  egg_cost_thb: number;
}

interface UnitCosts {
  pork_per_kg: number;
  basil_pack_g: number;
  basil_pack_thb: number;
  chili_per_100g_thb: number;
  garlic_per_500g_thb: number;
  rice_per_5kg_thb: number;
  oil_per_litre_thb: number;
  drink_cost_thb: number;
}

interface AttachRates {
  egg_rate: number;
  drink_rate: number;
}

interface Settings {
  floorSqm: number;
  diningPct: number;
  sqmPerGuest: number;
  seatTurnsPerDay: number;
  platePrice: number;
  eggAddPrice: number;
  drinkPrice: number;
  recipe: Recipe;
  unitCosts: UnitCosts;
  attach: AttachRates;
  rent_thb: number;
  son_salary_thb: number;
  owner_salary_thb: number;
  utilities_thb: number;
  useManualTurns: boolean;
  open_days_per_month: number;
}

// --- i18n (Thai / English) --------------------------------------------------
const L = {
  th: {
    title: "กะเพราเผ็ดมากกก - Scenario Dashboard",
    subtitle: "สลับดู Base / Best / Worst และปรับสมมติฐานเพื่อดูจุดคุ้มทุน",
    language: "ภาษา",
    inputs: "สมมติฐาน (Inputs)",
    quick: "ข้อมูลสำคัญ",
    site: "พื้นที่และที่นั่ง",
    menu: "ราคาและเมนู",
    recipe: "สูตรและวัตถุดิบ",
    attach: "อัตราซื้อเพิ่ม (Attach Rate)",
    fixed: "ต้นทุนคงที่",
    ops: "การดำเนินงาน",
    scenario: "สถานการณ์",
    base: "ฐาน",
    best: "ดีที่สุด",
    worst: "แย่สุด",
    reset: "รีเซ็ตตาม PRD",
    kpis: "ตัวชี้วัด",
    seats: "จำนวนที่นั่ง",
    covers: "ลูกค้า/วัน (คาดการณ์)",
    rev: "รายได้/เดือน",
    gp: "กำไรขั้นต้น/เดือน",
    np: "กำไรสุทธิ/เดือน",
    be: "เดือนที่คุ้มทุน",
    chartTitle: "กำไรสุทธิรายเดือน (12 เดือน)",
    ownerSalary: "เงินเดือนเจ้าของ (บ./เดือน)",
    staffSalary: "เงินเดือนพนักงาน",
    utilities: "ค่าสาธารณูปโภค+เบ็ดเตล็ด",
    yearChartTitle: "กำไรสุทธิรายปี",
    initialTitle: "เงินลงทุนตั้งต้น",
    invAssump: "สมมติ: มัดจำค่าเช่า {m} เดือน · สต็อกเริ่มต้น {d} วัน · เงินกันสำรอง {c} เดือน",
    deposit: "มัดจำ/ค่าเช่าล่วงหน้า",
    openingStock: "สต็อกวัตถุดิบเริ่มต้น",
    openingSupplies: "อุปกรณ์/ของใช้เริ่มต้น (ประมาณ)",
    cashBuffer: "เงินกันสำรองค่าใช้จ่ายคงที่",
    totalInit: "รวมเงินลงทุนตั้งต้น",
    yearsHorizon: "จำนวนปี",
    payroll: "เงินเดือน (Owner/Staff)",
  },
  en: {
    title: "Kapraw Ultra-Spicy - Scenario Dashboard",
    subtitle: "Toggle Base / Best / Worst and tweak assumptions to see breakeven",
    language: "Language",
    inputs: "Inputs",
    quick: "Quick Inputs",
    site: "Site & Seating",
    menu: "Pricing",
    recipe: "Recipe & Ingredients",
    attach: "Attach Rates",
    fixed: "Fixed Costs",
    ops: "Operations",
    scenario: "Scenarios",
    base: "Base",
    best: "Best",
    worst: "Worst",
    reset: "Reset to PRD",
    kpis: "KPIs",
    seats: "Seats",
    covers: "Covers/day (est)",
    rev: "Revenue/month",
    gp: "Gross Profit/month",
    np: "Net Profit/month",
    be: "Breakeven month",
    chartTitle: "Monthly Net Profit (12 months)",
    ownerSalary: "Owner salary (THB/month)",
    staffSalary: "Staff salary",
    utilities: "Utilities+misc",
    yearChartTitle: "Yearly Net Profit",
    initialTitle: "Initial Investment",
    invAssump: "Assumptions: rent deposit {m} mo · opening stock {d} days · cash buffer {c} mo",
    deposit: "Rent deposit/advance",
    openingStock: "Opening ingredient stock",
    openingSupplies: "Opening supplies (approx)",
    cashBuffer: "Cash buffer for fixed costs",
    totalInit: "Total initial investment",
    yearsHorizon: "Years horizon",
    payroll: "Payroll (Owner/Staff)",
  },
} as const;

type LangKey = keyof typeof L;

// Ingredient labels per language
const IL: Record<LangKey, Record<string, string>> = {
  th: {
    pork_g: "หมูสับ (กรัม)",
    pork_per_kg: "หมูสับ บาท/กก.",
    basil_g: "ใบกะเพรา (กรัม)",
    basil_pack_g: "กะเพรา/แพ็ค (กรัม)",
    basil_pack_thb: "กะเพรา/แพ็ค (บ.)",
    chili_g: "พริก (กรัม)",
    chili_per_100g_thb: "พริก บาท/100ก.",
    garlic_g: "กระเทียม (กรัม)",
    garlic_per_500g_thb: "กระเทียม บาท/500ก.",
    rice_g: "ข้าว (กรัม)",
    rice_per_5kg_thb: "ข้าว บาท/5กก.",
    oil_ml: "น้ำมัน (มล.)",
    oil_per_litre_thb: "น้ำมัน บาท/ลิตร",
    sauces_thb: "ซอส/เครื่องปรุง (บ./จาน)",
    egg_cost_thb: "ต้นทุนไข่ดาว (บ.)",
    drink_cost_thb: "ต้นทุนโค้ก/กระป๋อง (บ.)",
  },
  en: {
    pork_g: "Pork (g)",
    pork_per_kg: "Pork THB/kg",
    basil_g: "Basil (g)",
    basil_pack_g: "Basil pack g",
    basil_pack_thb: "Basil pack THB",
    chili_g: "Chili (g)",
    chili_per_100g_thb: "Chili THB/100g",
    garlic_g: "Garlic (g)",
    garlic_per_500g_thb: "Garlic THB/500g",
    rice_g: "Rice (g)",
    rice_per_5kg_thb: "Rice THB/5kg",
    oil_ml: "Oil (ml)",
    oil_per_litre_thb: "Oil THB/L",
    sauces_thb: "Sauces THB/plate",
    egg_cost_thb: "Egg cost (THB)",
    drink_cost_thb: "Coke cost/can (THB)",
  },
};

// --- Default PRD-ish numbers ------------------------------------------------
const DEFAULTS: Settings = {
  // Site
  floorSqm: 60,
  diningPct: 60,
  sqmPerGuest: 1.2,
  seatTurnsPerDay: 2.5,

  // Pricing
  platePrice: 70,
  eggAddPrice: 10,
  drinkPrice: 25,

  // Recipe (grams / ml per plate)
  recipe: {
    pork_g: 100,
    basil_g: 10,
    chili_g: 5,
    garlic_g: 5,
    rice_g: 200,
    oil_ml: 15,
    sauces_thb: 3,
    egg_cost_thb: 5,
  },

  // Ingredient unit costs
  unitCosts: {
    pork_per_kg: 129,
    basil_pack_g: 150,
    basil_pack_thb: 15,
    chili_per_100g_thb: 17,
    garlic_per_500g_thb: 45,
    rice_per_5kg_thb: 132,
    oil_per_litre_thb: 48.92,
    drink_cost_thb: 13,
  },

  // Attach rates
  attach: {
    egg_rate: 0.5,
    drink_rate: 0.0,
  },

  // Fixed costs
  rent_thb: 78000,
  son_salary_thb: 15000,
  owner_salary_thb: 20000,
  utilities_thb: 10000,

  // Ops
  useManualTurns: false,
  open_days_per_month: 30,
};

// Scenario presets (with compounding dynamics)
interface Scenario {
  id: string;
  labelKey: string;
  occ: number;
  turns: number;
  monthlyCoversGrowth: number;
  priceAdj: number;
  cogsAdj: number;
  rentAdj: number;
  monthlyPriceGrowth: number;
  monthlyCogsInflation: number;
  monthlyFixedInflation: number;
}

const SCENARIOS: Record<string, Scenario> = {
  base: {
    id: "base",
    labelKey: "base",
    occ: 0.6,
    turns: 2.5,
    monthlyCoversGrowth: 0.02,
    priceAdj: 0.0,
    cogsAdj: 0.0,
    rentAdj: 0.0,
    monthlyPriceGrowth: 0.002,
    monthlyCogsInflation: 0.002,
    monthlyFixedInflation: 0.0015,
  },
  best: {
    id: "best",
    labelKey: "best",
    occ: 0.8,
    turns: 3.0,
    monthlyCoversGrowth: 0.04,
    priceAdj: 0.05,
    cogsAdj: -0.05,
    rentAdj: -0.05,
    monthlyPriceGrowth: 0.0035,
    monthlyCogsInflation: 0.0015,
    monthlyFixedInflation: 0.001,
  },
  worst: {
    id: "worst",
    labelKey: "worst",
    occ: 0.4,
    turns: 2.0,
    monthlyCoversGrowth: -0.01,
    priceAdj: -0.05,
    cogsAdj: 0.05,
    rentAdj: 0.05,
    monthlyPriceGrowth: -0.002,
    monthlyCogsInflation: 0.005,
    monthlyFixedInflation: 0.003,
  },
};

// --- Core calcs -------------------------------------------------------------
function computeSeats(floorSqm: number, diningPct: number, sqmPerGuest: number): { diningArea: number; seats: number } {
  const diningArea = (floorSqm * clamp(diningPct, 10, 90)) / 100;
  const seats = Math.max(2, Math.floor(diningArea / Math.max(0.8, sqmPerGuest)));
  return { diningArea, seats };
}

function computeCOGSPerPlate(recipe: Recipe, unit: UnitCosts, adj = 0): number {
  const pork = (recipe.pork_g / 1000) * unit.pork_per_kg;
  const basil = (recipe.basil_g / unit.basil_pack_g) * unit.basil_pack_thb;
  const chili = (recipe.chili_g / 100) * unit.chili_per_100g_thb;
  const garlic = (recipe.garlic_g / 500) * unit.garlic_per_500g_thb;
  const rice = (recipe.rice_g / 5000) * unit.rice_per_5kg_thb;
  const oil = (recipe.oil_ml / 1000) * unit.oil_per_litre_thb;
  const sauces = recipe.sauces_thb;
  const plate = pork + basil + chili + garlic + rice + oil + sauces;
  return plate * (1 + adj);
}

function effectiveSeatTurns(settings: Settings, scenario: Scenario): number {
  if (settings.useManualTurns) {
    const anchor = settings.seatTurnsPerDay;
    const preset = scenario.turns ?? DEFAULTS.seatTurnsPerDay;
    const base = DEFAULTS.seatTurnsPerDay;
    return anchor * (preset / base);
  }
  return scenario.turns ?? settings.seatTurnsPerDay;
}

interface MonthlyResult {
  month: number;
  seats: number;
  dailyCovers: number;
  revenue: number;
  gross: number;
  net: number;
  cumulative: number;
}

interface ForecastResult {
  rows: MonthlyResult[];
  breakMonth: number | null;
  seats: number;
  baseDailyCovers: number;
}

function forecast12(settings: Settings, scenario: Scenario): ForecastResult {
  const { seats } = computeSeats(settings.floorSqm, settings.diningPct, settings.sqmPerGuest);
  const seatTurns = effectiveSeatTurns(settings, scenario);
  const occ = scenario.occ;
  const baseDailyCovers = Math.max(1, Math.floor(seats * seatTurns * occ));

  const price0 = settings.platePrice * (1 + (scenario.priceAdj || 0));
  let price = price0;
  const cogs0 = computeCOGSPerPlate(settings.recipe, settings.unitCosts, scenario.cogsAdj || 0);
  let cogsPlate = cogs0;
  const eggRev = settings.eggAddPrice;
  const eggCost = settings.recipe.egg_cost_thb;
  const drinkRev = settings.drinkPrice;
  const drinkCost = settings.unitCosts.drink_cost_thb;

  const fixedBase = settings.rent_thb * (1 + (scenario.rentAdj || 0)) + settings.son_salary_thb + settings.utilities_thb + (settings.owner_salary_thb > 0 ? settings.owner_salary_thb : 0);
  let fixed = fixedBase;
  const days = settings.open_days_per_month;

  const months = Array.from({ length: 12 }, (_, i) => i + 1);
  let covers = baseDailyCovers;
  let cum = 0;
  let breakMonth: number | null = null;

  const rows = months.map((m) => {
    const monthlyCovers = Math.round(covers * days);
    const avgCheck = price + settings.attach.egg_rate * eggRev + settings.attach.drink_rate * drinkRev;
    const avgCost = cogsPlate + settings.attach.egg_rate * eggCost + settings.attach.drink_rate * drinkCost;

    const revenue = monthlyCovers * avgCheck;
    const cogs = monthlyCovers * avgCost;
    const gross = revenue - cogs;
    const net = gross - fixed;

    cum += net;
    if (!breakMonth && cum >= 0) breakMonth = m;

    covers = Math.max(1, covers * (1 + (scenario.monthlyCoversGrowth || 0)));
    price *= 1 + (scenario.monthlyPriceGrowth || 0);
    cogsPlate *= 1 + (scenario.monthlyCogsInflation || 0);
    fixed *= 1 + (scenario.monthlyFixedInflation || 0);

    return { month: m, seats, dailyCovers: Math.round(covers), revenue, gross, net, cumulative: cum };
  });

  return { rows, breakMonth, seats, baseDailyCovers };
}

interface ExtendedMonthlyResult {
  month: number;
  revenue: number;
  gross: number;
  net: number;
  cumulative: number;
}

interface ExtendedForecastResult {
  rows: ExtendedMonthlyResult[];
}

function forecastN(settings: Settings, scenario: Scenario, monthsCount: number): ExtendedForecastResult {
  const { seats } = computeSeats(settings.floorSqm, settings.diningPct, settings.sqmPerGuest);
  const seatTurns = effectiveSeatTurns(settings, scenario);
  const occ = scenario.occ;
  const baseDailyCovers = Math.max(1, Math.floor(seats * seatTurns * occ));

  const price0 = settings.platePrice * (1 + (scenario.priceAdj || 0));
  let price = price0;
  const cogs0 = computeCOGSPerPlate(settings.recipe, settings.unitCosts, scenario.cogsAdj || 0);
  let cogsPlate = cogs0;
  const eggRev = settings.eggAddPrice;
  const eggCost = settings.recipe.egg_cost_thb;
  const drinkRev = settings.drinkPrice;
  const drinkCost = settings.unitCosts.drink_cost_thb;

  const fixedBase = settings.rent_thb * (1 + (scenario.rentAdj || 0)) + settings.son_salary_thb + settings.utilities_thb + (settings.owner_salary_thb > 0 ? settings.owner_salary_thb : 0);
  let fixed = fixedBase;
  const days = settings.open_days_per_month;

  const months = Array.from({ length: monthsCount }, (_, i) => i + 1);
  let covers = baseDailyCovers;
  let cum = 0;

  const rows = months.map((m) => {
    const monthlyCovers = Math.round(covers * days);
    const avgCheck = price + settings.attach.egg_rate * eggRev + settings.attach.drink_rate * drinkRev;
    const avgCost = cogsPlate + settings.attach.egg_rate * eggCost + settings.attach.drink_rate * drinkCost;
    const revenue = monthlyCovers * avgCheck;
    const cogs = monthlyCovers * avgCost;
    const gross = revenue - cogs;
    const net = gross - fixed;
    cum += net;

    covers = Math.max(1, covers * (1 + (scenario.monthlyCoversGrowth || 0)));
    price *= 1 + (scenario.monthlyPriceGrowth || 0);
    cogsPlate *= 1 + (scenario.monthlyCogsInflation || 0);
    fixed *= 1 + (scenario.monthlyFixedInflation || 0);

    return { month: m, revenue, gross, net, cumulative: cum };
  });

  return { rows };
}

// --- Initial investment (derived, non-editable) ---------------------------
const INIT_ASSUMP = { depositMonths: 2, inventoryDays: 7, cashBufferMonths: 1 } as const;

interface InitialInvestment {
  deposit: number;
  openingStock: number;
  supplies: number;
  cashBuffer: number;
  total: number;
  dailyCovers: number;
  avgCost: number;
}

function computeInitialInvestment(settings: Settings, scenario: Scenario): InitialInvestment {
  const { seats } = computeSeats(settings.floorSqm, settings.diningPct, settings.sqmPerGuest);
  const seatTurns = effectiveSeatTurns(settings, scenario);
  const dailyCovers = Math.max(1, Math.floor(seats * seatTurns * scenario.occ));

  const plateCost = computeCOGSPerPlate(settings.recipe, settings.unitCosts, scenario.cogsAdj || 0);
  const eggCost = settings.recipe.egg_cost_thb;
  const drinkCost = settings.unitCosts.drink_cost_thb;
  const avgCost = plateCost + settings.attach.egg_rate * eggCost + settings.attach.drink_rate * drinkCost;

  const deposit = settings.rent_thb * INIT_ASSUMP.depositMonths;
  const openingStock = avgCost * dailyCovers * INIT_ASSUMP.inventoryDays;
  const fixedMonthly = settings.rent_thb * (1 + (scenario.rentAdj || 0)) + settings.son_salary_thb + settings.utilities_thb + (settings.owner_salary_thb > 0 ? settings.owner_salary_thb : 0);
  const cashBuffer = fixedMonthly * INIT_ASSUMP.cashBufferMonths;
  const supplies = settings.utilities_thb; // proxy for opening smallwares
  const total = Math.round(deposit + openingStock + cashBuffer + supplies);
  return { deposit, openingStock, supplies, cashBuffer, total, dailyCovers, avgCost };
}

// --- UI Components ----------------------------------------------------------
function RealSmartLogo() {
  return (
    <div className="flex items-center gap-2">
      <img 
        src="/Realsmart_logo.png" 
        alt="RealSmart Logo" 
        width="40" 
        height="40" 
        className="flex-shrink-0 object-contain"
      />
      <span className="text-lg font-bold text-neutral-300 hidden sm:block">RealSmart Academy</span>
    </div>
  );
}

interface SectionProps {
  title: React.ReactNode;
  children: React.ReactNode;
}

function Section({ title, children }: SectionProps) {
  return (
    <div className="rounded-2xl border border-neutral-800 p-4 h-full">
      <div className="mb-3 text-neutral-300 font-semibold tracking-tight">{title}</div>
      <div className="space-y-3">{children}</div>
    </div>
  );
}

interface SubTitleProps {
  label: React.ReactNode;
}

function SubTitle({ label }: SubTitleProps) {
  return <div className="text-xs uppercase tracking-wide text-neutral-400">{label}</div>;
}

interface KProps {
  label: React.ReactNode;
  value: React.ReactNode;
  tiny?: boolean;
  tone?: "neg" | "pos";
}

function K({ label, value, tiny, tone }: KProps) {
  return (
    <div className={`rounded-xl border ${tone === "neg" ? "border-rose-600/40 bg-rose-500/5" : "border-neutral-800 bg-neutral-900/50"} p-3 ${tiny ? "py-2 px-3" : ""}`}>
      <div className="text-xs text-neutral-400">{label}</div>
      <div className={`font-semibold ${tiny ? "text-base" : "text-xl"} tabular-nums whitespace-nowrap`}>{value}</div>
    </div>
  );
}

interface CardProps {
  title: React.ReactNode;
  value: React.ReactNode;
  tone?: "neg" | "pos";
}

function Card({ title, value, tone }: CardProps) {
  return (
    <div className={`rounded-2xl p-4 border ${tone === "neg" ? "border-rose-600/40 bg-rose-500/5" : "border-neutral-800 bg-neutral-900/50"}`}>
      <div className="text-sm text-neutral-400">{title}</div>
      <div className={`font-semibold mt-1 tabular-nums whitespace-nowrap text-xl sm:text-2xl ${tone === "neg" ? "text-rose-300" : ""}`}>
        <span className="whitespace-nowrap tabular-nums">{value}</span>
      </div>
    </div>
  );
}

interface NumberInputProps {
  label: React.ReactNode;
  value: number;
  onChange: (n: number) => void;
  min?: number;
  max?: number;
  step?: number;
  disabled?: boolean;
}

function NumberInput({ label, value, onChange, min = 0, max = 1e9, step = 1, disabled = false }: NumberInputProps) {
  return (
    <label className="flex items-center justify-between gap-3 text-sm">
      <span className="text-neutral-300">{label}</span>
      <input
        type="number"
        className="w-44 bg-neutral-900 border border-neutral-800 rounded-xl px-3 py-2 text-right"
        value={value}
        min={min}
        max={max}
        step={step}
        disabled={disabled}
        onChange={(e) => onChange(Number(e.target.value))}
      />
    </label>
  );
}

interface RangeInputProps {
  label: React.ReactNode;
  value: number;
  onChange: (n: number) => void;
  min?: number;
  max?: number;
  step?: number;
  disabled?: boolean;
}

function RangeInput({ label, value, onChange, min = 0, max = 100, step = 1, disabled = false }: RangeInputProps) {
  return (
    <div className="text-sm">
      <div className="flex items-center justify-between">
        <span className="text-neutral-300">{label}</span>
        <span className="text-neutral-400 tabular-nums w-16 text-right">
          {typeof value === "number" ? (max === 100 && min === 0 ? value.toFixed(0) : value.toFixed(1)) : value}
          {max === 100 && min === 0 ? '%' : ''}
        </span>
      </div>
      <input
        type="range"
        className="w-full accent-emerald-400"
        value={value}
        min={min}
        max={max}
        step={step}
        disabled={disabled}
        onChange={(e) => onChange(Number(e.target.value))}
      />
    </div>
  );
}

interface AutoCOGSProps {
  lang: LangKey;
  settings: Settings;
  scenario: Scenario;
}

function AutoCOGS({ lang, settings, scenario }: AutoCOGSProps) {
  const plateCost = computeCOGSPerPlate(settings.recipe, settings.unitCosts, scenario.cogsAdj || 0);
  const eggCost = settings.recipe.egg_cost_thb;
  const drinkCost = settings.unitCosts.drink_cost_thb;
  const avgCost = plateCost + settings.attach.egg_rate * eggCost + settings.attach.drink_rate * drinkCost;
  const price = settings.platePrice * (1 + (scenario.priceAdj || 0));
  const avgRev = price + settings.attach.egg_rate * settings.eggAddPrice + settings.attach.drink_rate * settings.drinkPrice;
  const gpPct = avgRev > 0 ? ((avgRev - avgCost) / avgRev) * 100 : 0;

  return (
    <div className="rounded-xl border border-neutral-800 p-3 mt-2 bg-neutral-900/40">
      <div className="grid grid-cols-2 gap-3 text-sm">
        <K tiny label={lang === "th" ? "ต้นทุน/จาน (เฉลี่ย)" : "Avg COGS/plate"} value={`${fmt(Math.round(avgCost))} ฿`} />
        <K tiny label={lang === "th" ? "รายได้/จาน (เฉลี่ย)" : "Avg revenue/plate"} value={`${fmt(Math.round(avgRev))} ฿`} />
        <K tiny label="GP%" value={`${gpPct.toFixed(1)}%`} />
      </div>
      <div className="text-xs text-neutral-500 mt-2">
        {lang === "th"
          ? "* คิดจากสูตรและราคาต่อหน่วยที่กรอก + อัตราสั่งไข่ดาว/เครื่องดื่ม"
          : "* Based on recipe & unit costs plus egg/drink attach rates"}
      </div>
    </div>
  );
}

export default function App() {
  const [lang, setLang] = useState<LangKey>("th");
  const T = L[lang];

  const [settings, setSettings] = useState<Settings>(DEFAULTS);
  const [scenarioId, setScenarioId] = useState<keyof typeof SCENARIOS>("base");
  const [years, setYears] = useState<number>(5);

  const resultBase = useMemo(() => forecast12(settings, SCENARIOS.base), [settings]);
  const resultBest = useMemo(() => forecast12(settings, SCENARIOS.best), [settings]);
  const resultWorst = useMemo(() => forecast12(settings, SCENARIOS.worst), [settings]);

  const active = scenarioId === "base" ? resultBase : scenarioId === "best" ? resultBest : resultWorst;
  const scenario = SCENARIOS[scenarioId];

  const chartData = resultBase.rows.map((r, idx) => ({
    month: `M${r.month}`,
    base: r.net,
    best: resultBest.rows[idx].net,
    worst: resultWorst.rows[idx].net,
  }));

  const extBase = useMemo(() => forecastN(settings, SCENARIOS.base, years * 12), [settings, years]);
  const extBest = useMemo(() => forecastN(settings, SCENARIOS.best, years * 12), [settings, years]);
  const extWorst = useMemo(() => forecastN(settings, SCENARIOS.worst, years * 12), [settings, years]);
  
  const yearData = useMemo(() => Array.from({ length: years }, (_, i) => {
    const s = i * 12; 
    const e = (i + 1) * 12;
    return {
      year: `Y${i + 1}`,
      base: extBase.rows.slice(s, e).reduce((a, b) => a + b.net, 0),
      best: extBest.rows.slice(s, e).reduce((a, b) => a + b.net, 0),
      worst: extWorst.rows.slice(s, e).reduce((a, b) => a + b.net, 0),
    };
  }), [extBase, extBest, extWorst, years]);

  const activeExt = useMemo(() => forecastN(settings, SCENARIOS[scenarioId], years * 12), [settings, scenarioId, years]);
  
  const breakLabel = useMemo(() => {
    if (active.breakMonth) return `M${active.breakMonth}`;
    const idx = activeExt.rows.findIndex((r) => r.cumulative >= 0);
    if (idx >= 0) {
      const m = idx + 1; 
      const y = Math.ceil(m / 12);
      return `M${m} (Y${y})`;
    }
    return lang === "th" ? `ยังไม่คุ้มทุนใน ${years} ปี` : `No breakeven within ${years} years`;
  }, [active.breakMonth, activeExt, years, lang]);

  const baseTurns = effectiveSeatTurns(settings, SCENARIOS.base);
  const bestTurns = effectiveSeatTurns(settings, SCENARIOS.best);
  const worstTurns = effectiveSeatTurns(settings, SCENARIOS.worst);

  const kpi = active.rows[active.rows.length - 1] || { revenue: 0, gross: 0, net: 0 };
  const init = useMemo(() => computeInitialInvestment(settings, SCENARIOS[scenarioId]), [settings, scenarioId]);

  const update = (path: string, value: any) => {
    setSettings((s) => {
      const copy = JSON.parse(JSON.stringify(s));
      const segs = path.split(".");
      let cur = copy;
      for (let i = 0; i < segs.length - 1; i++) cur = cur[segs[i]];
      cur[segs[segs.length - 1]] = value;
      return copy;
    });
  };

  const reset = () => setSettings(DEFAULTS);

  return (
    <div className="min-h-screen w-full bg-neutral-950 text-neutral-100">
      <div className="mx-auto max-w-7xl px-4 py-6">
        {/* Header */}
        <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight">{T.title}</h1>
            <p className="text-neutral-400">{T.subtitle}</p>
          </div>
          <div className="flex items-center gap-4">
            <RealSmartLogo />
            <div className="flex items-center gap-2">
              <label className="text-sm text-neutral-400">{T.language}</label>
              <select
                className="bg-neutral-900 border border-neutral-800 rounded-xl px-3 py-2"
                value={lang}
                onChange={(e) => setLang(e.target.value as LangKey)}
              >
                <option value="th">ไทย</option>
                <option value="en">EN</option>
              </select>
            </div>
          </div>
        </div>

        {/* Scenario toggles */}
        <div className="mt-4 flex flex-wrap gap-2">
          {(["base", "best", "worst"] as const).map((id) => (
            <button
              key={id}
              onClick={() => setScenarioId(id)}
              className={`px-4 py-2 rounded-2xl border transition ${
                scenarioId === id
                  ? "bg-emerald-500/20 border-emerald-500 text-emerald-300"
                  : id === "best"
                  ? "bg-neutral-900 border-neutral-700 hover:border-emerald-400"
                  : id === "worst"
                  ? "bg-neutral-900 border-neutral-700 hover:border-rose-400"
                  : "bg-neutral-900 border-neutral-700 hover:border-sky-400"
              }`}
            >
              {lang === "th" ? T[id as keyof typeof T] : L.en[id as keyof typeof L.en]}
            </button>
          ))}
          <button onClick={reset} className="px-4 py-2 rounded-2xl border border-neutral-700 hover:border-neutral-500">
            {T.reset}
          </button>
        </div>

        {/* ===== Symmetric Rows ================================================= */}
        <div className="mt-6 grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* Row 1: Quick Inputs (L) vs KPIs+Breakeven (R) */}
          <div className="lg:col-span-6">
            <Section title={`${T.inputs} · ${T.quick}`}>
              <div className="grid grid-cols-2 gap-3">
                <NumberInput label={lang === "th" ? "ราคาขาย/จาน (บาท)" : "Plate price (THB)"} value={settings.platePrice} onChange={(v) => update("platePrice", v)} min={40} max={150} step={1} />
                <NumberInput label={lang === "th" ? "ค่าเช่า/เดือน" : "Rent/month"} value={settings.rent_thb} onChange={(v)=>update("rent_thb", v)} min={20000} max={200000} step={1000} />
                <RangeInput label={lang === "th" ? "สัดส่วนสั่ง +ไข่ดาว" : "+Egg attach rate"} value={settings.attach.egg_rate * 100} onChange={(v)=>update("attach.egg_rate", v/100)} min={0} max={100} step={5} />
                <RangeInput label={lang === "th" ? "สัดส่วนสั่งเครื่องดื่ม" : "Drink attach rate"} value={settings.attach.drink_rate * 100} onChange={(v)=>update("attach.drink_rate", v/100)} min={0} max={100} step={5} />
                <NumberInput label={lang === "th" ? "ราคาขายไข่ดาว (บ.)" : "Egg add-on price (THB)"} value={settings.eggAddPrice} onChange={(v)=>update("eggAddPrice", v)} min={0} max={50} step={1} />
                <NumberInput label={lang === "th" ? "ราคาขายเครื่องดื่ม (บ.)" : "Drink price (THB)"} value={settings.drinkPrice} onChange={(v)=>update("drinkPrice", v)} min={0} max={200} step={1} />
              </div>

              <div className="mt-3 grid grid-cols-2 gap-3">
                <K tiny label={T.seats} value={computeSeats(settings.floorSqm, settings.diningPct, settings.sqmPerGuest).seats} />
                <K tiny label={T.covers} value={Math.floor(
                  computeSeats(settings.floorSqm, settings.diningPct, settings.sqmPerGuest).seats *
                  effectiveSeatTurns(settings, SCENARIOS[scenarioId]) *
                  SCENARIOS[scenarioId].occ
                )} />
              </div>

              <div className="rounded-xl border border-neutral-800 p-3 bg-neutral-900/40">
                <div className="text-xs text-neutral-400 mb-2">{lang === "th" ? "สมมติฐานอัตราการใช้ที่นั่ง/รอบหมุน" : "Occupancy by scenario"}</div>
                <div className="grid grid-cols-3 gap-2 text-sm">
                  <K tiny label={`${lang === 'th' ? T.base : 'Base'} · ${lang==='th'?'อัตราใช้ที่นั่ง':'Occ'}`} value={`${Math.round(SCENARIOS.base.occ*100)}% · ${baseTurns.toFixed(1)}x`} />
                  <K tiny label={`${lang === 'th' ? T.best : 'Best'} · ${lang==='th'?'อัตราใช้ที่นั่ง':'Occ'}`} value={`${Math.round(SCENARIOS.best.occ*100)}% · ${bestTurns.toFixed(1)}x`} />
                  <K tiny label={`${lang === 'th' ? T.worst : 'Worst'} · ${lang==='th'?'อัตราใช้ที่นั่ง':'Occ'}`} value={`${Math.round(SCENARIOS.worst.occ*100)}% · ${worstTurns.toFixed(1)}x`} />
                </div>
                <div className="text-[11px] text-neutral-500 mt-2">{lang === 'th' ? 'คำนวณ: ที่นั่ง × รอบหมุน/วัน × อัตราใช้ที่นั่ง = ลูกค้า/วัน (ประมาณ)' : 'Formula: seats × turns/day × occupancy = estimated covers/day'}</div>
              </div>

              <AutoCOGS lang={lang} settings={settings} scenario={scenario} />
            </Section>
          </div>
          <div className="lg:col-span-6">
            <Section title={T.kpis}>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Card title={T.seats} value={fmt(active.seats)} />
                <Card title={T.covers} value={fmt(active.baseDailyCovers)} />
                <Card title={T.rev} value={`${fmt(Math.round(kpi.revenue))} ฿`} />
                <Card title={T.np} value={`${fmt(Math.round(kpi.net))} ฿`} tone={kpi.net>=0?"pos":"neg"} />
              </div>
              <div className="mt-4 rounded-xl border border-neutral-800 p-3">
                <div className="text-sm text-neutral-400 mb-1">{T.be}</div>
                <div className="text-2xl font-semibold tabular-nums whitespace-nowrap">{breakLabel}</div>
              </div>
              {/* Initial investment breakdown (derived) */}
              <div className="mt-4 rounded-xl border border-neutral-800 p-3 bg-neutral-900/40">
                <div className="text-sm text-neutral-300 font-medium mb-2">{T.initialTitle}</div>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <K tiny label={T.deposit} value={`${fmt(Math.round(init.deposit))} ฿`} />
                  <K tiny label={T.openingStock} value={`${fmt(Math.round(init.openingStock))} ฿`} />
                  <K tiny label={T.openingSupplies} value={`${fmt(Math.round(init.supplies))} ฿`} />
                  <K tiny label={T.cashBuffer} value={`${fmt(Math.round(init.cashBuffer))} ฿`} />
                </div>
                <div className="mt-2 rounded-lg border border-neutral-800 p-2 flex items-center justify-between">
                  <div className="text-neutral-400 text-sm">{T.totalInit}</div>
                  <div className="text-lg font-semibold tabular-nums">{fmt(init.total)} ฿</div>
                </div>
                <div className="text-[11px] text-neutral-500 mt-2">
                  {T.invAssump
                    .replace('{m}', String(INIT_ASSUMP.depositMonths))
                    .replace('{d}', String(INIT_ASSUMP.inventoryDays))
                    .replace('{c}', String(INIT_ASSUMP.cashBufferMonths))}
                </div>
              </div>
            </Section>
          </div>

          {/* Row 2: Site+Fixed+Ops (L) vs Monthly Chart (R) */}
          <div className="lg:col-span-6">
            <Section title={`${T.inputs} · ${T.site} + ${T.fixed}/${T.ops}`}>
              <div className="space-y-4">
                {/* Site & seating */}
                <div className="grid grid-cols-2 gap-3">
                  <NumberInput label={lang === "th" ? "พื้นที่ (ตร.ม.)" : "Floor area (sqm)"} value={settings.floorSqm} onChange={(v) => update("floorSqm", v)} min={20} max={200} step={1} />
                  <RangeInput label={lang === "th" ? "% พื้นที่นั่งทาน" : "% Dining area"} value={settings.diningPct} onChange={(v) => update("diningPct", v)} min={30} max={80} step={1} />
                  <RangeInput label={lang === "th" ? "ตร.ม./ที่นั่ง" : "sqm per guest"} value={settings.sqmPerGuest} onChange={(v) => update("sqmPerGuest", v)} min={0.9} max={1.6} step={0.1} />
                  <RangeInput label={lang === "th" ? "รอบหมุนที่นั่ง/วัน" : "Seat turns/day"} value={settings.useManualTurns ? settings.seatTurnsPerDay : effectiveSeatTurns(settings, SCENARIOS[scenarioId])} onChange={(v) => update("seatTurnsPerDay", v)} min={1.5} max={4} step={0.1} disabled={!settings.useManualTurns} />
                </div>
                <div className="text-xs text-neutral-400 flex items-center gap-3">
                  <label className="inline-flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" className="accent-emerald-400" checked={settings.useManualTurns} onChange={(e)=>update("useManualTurns", e.target.checked)} />
                    <span>{lang==="th"?"กำหนดรอบหมุนเอง":"Manual seat turns"}</span>
                  </label>
                  <span className="opacity-70">{lang==="th"?(settings.useManualTurns?"ระบบจะใช้ค่าที่ตั้งเอง":"ระบบจะใช้ค่าจากสถานการณ์ที่เลือก"):(settings.useManualTurns?"Using your value":"Using scenario preset")}</span>
                </div>
                {/* Fixed + Ops (combined incl. salaries) */}
                <div className="grid grid-cols-2 gap-3 border-t border-neutral-800 pt-3">
                  <NumberInput label={T.ownerSalary} value={settings.owner_salary_thb} onChange={(v)=>update("owner_salary_thb", v)} min={0} max={100000} step={500} />
                  <NumberInput label={T.staffSalary} value={settings.son_salary_thb} onChange={(v)=>update("son_salary_thb", v)} min={0} max={50000} step={500} />
                  <NumberInput label={T.utilities} value={settings.utilities_thb} onChange={(v)=>update("utilities_thb", v)} min={0} max={50000} step={500} />
                  <NumberInput label={lang === "th" ? "เปิดบริการ (วัน/เดือน)" : "Open days/month"} value={settings.open_days_per_month} onChange={(v)=>update("open_days_per_month", v)} min={20} max={31} step={1} />
                </div>
              </div>
            </Section>
          </div>
          <div className="lg:col-span-6">
            <Section title={T.chartTitle}>
              <div className="h-72 w-full">
                <ResponsiveContainer>
                  <LineChart data={chartData} margin={{ top: 10, right: 24, bottom: 10, left: 8 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#262626" />
                    <XAxis dataKey="month" stroke="#9ca3af" tick={{ fontSize: 12 }} tickMargin={8} />
                    <YAxis width={112} stroke="#9ca3af" tick={{ fontSize: 12 }} tickFormatter={(v)=>fmt(v)} />
                    <Tooltip formatter={(v)=>`${fmt(Math.round(v as number))} ฿`} />
                    <Legend />
                    <Line type="monotone" dataKey="base" name={lang==="th"?"ฐาน":"Base"} stroke="#60a5fa" strokeWidth={2} dot={false} />
                    <Line type="monotone" dataKey="best" name={lang==="th"?"ดีที่สุด":"Best"} stroke="#34d399" strokeWidth={2} dot={false} />
                    <Line type="monotone" dataKey="worst" name={lang==="th"?"แย่สุด":"Worst"} stroke="#f43f5e" strokeWidth={2} dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </Section>
          </div>

          {/* Row 3: Recipe (L) vs Yearly Chart + Table (R) */}
          <div className="lg:col-span-6">
            <Section title={`${T.inputs} · ${T.recipe}`}>
              <div className="grid grid-cols-2 gap-3">
                <SubTitle label={lang === "th" ? "สูตรต่อจาน" : "Recipe per plate"} />
                <SubTitle label={lang === "th" ? "ราคาต่อหน่วย" : "Unit costs"} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <NumberInput label={IL[lang].pork_g} value={settings.recipe.pork_g} onChange={(v)=>update("recipe.pork_g", v)} min={60} max={150} step={5} />
                <NumberInput label={IL[lang].pork_per_kg} value={settings.unitCosts.pork_per_kg} onChange={(v)=>update("unitCosts.pork_per_kg", v)} min={80} max={200} step={1} />
                <NumberInput label={IL[lang].basil_g} value={settings.recipe.basil_g} onChange={(v)=>update("recipe.basil_g", v)} min={5} max={30} step={1} />
                <div className="grid grid-cols-2 gap-3">
                  <NumberInput label={IL[lang].basil_pack_g} value={settings.unitCosts.basil_pack_g} onChange={(v)=>update("unitCosts.basil_pack_g", v)} min={50} max={300} step={10} />
                  <NumberInput label={IL[lang].basil_pack_thb} value={settings.unitCosts.basil_pack_thb} onChange={(v)=>update("unitCosts.basil_pack_thb", v)} min={5} max={60} step={1} />
                </div>
                <NumberInput label={IL[lang].chili_g} value={settings.recipe.chili_g} onChange={(v)=>update("recipe.chili_g", v)} min={1} max={15} step={1} />
                <NumberInput label={IL[lang].chili_per_100g_thb} value={settings.unitCosts.chili_per_100g_thb} onChange={(v)=>update("unitCosts.chili_per_100g_thb", v)} min={8} max={60} step={1} />
                <NumberInput label={IL[lang].garlic_g} value={settings.recipe.garlic_g} onChange={(v)=>update("recipe.garlic_g", v)} min={1} max={20} step={1} />
                <NumberInput label={IL[lang].garlic_per_500g_thb} value={settings.unitCosts.garlic_per_500g_thb} onChange={(v)=>update("unitCosts.garlic_per_500g_thb", v)} min={20} max={120} step={1} />
                <NumberInput label={IL[lang].rice_g} value={settings.recipe.rice_g} onChange={(v)=>update("recipe.rice_g", v)} min={120} max={300} step={10} />
                <NumberInput label={IL[lang].rice_per_5kg_thb} value={settings.unitCosts.rice_per_5kg_thb} onChange={(v)=>update("unitCosts.rice_per_5kg_thb", v)} min={100} max={400} step={1} />
                <NumberInput label={IL[lang].oil_ml} value={settings.recipe.oil_ml} onChange={(v)=>update("recipe.oil_ml", v)} min={5} max={30} step={1} />
                <NumberInput label={IL[lang].oil_per_litre_thb} value={settings.unitCosts.oil_per_litre_thb} onChange={(v)=>update("unitCosts.oil_per_litre_thb", v)} min={20} max={120} step={0.1} />
                <NumberInput label={IL[lang].sauces_thb} value={settings.recipe.sauces_thb} onChange={(v)=>update("recipe.sauces_thb", v)} min={0} max={10} step={0.5} />
                <NumberInput label={IL[lang].egg_cost_thb} value={settings.recipe.egg_cost_thb} onChange={(v)=>update("recipe.egg_cost_thb", v)} min={3} max={10} step={0.5} />
                <div className="col-span-2 border-t border-neutral-800 pt-3">
                  <NumberInput label={IL[lang].drink_cost_thb} value={settings.unitCosts.drink_cost_thb} onChange={(v)=>update("unitCosts.drink_cost_thb", v)} min={8} max={20} step={1} />
                </div>
              </div>
            </Section>
          </div>
          <div className="lg:col-span-6">
            <Section title={<div className="flex items-center justify-between w-full"><span>{T.yearChartTitle}</span><span className="flex items-center gap-2"><label className="text-xs text-neutral-500">{T.yearsHorizon}</label><select className="bg-neutral-900 border border-neutral-800 rounded-xl px-2 py-1" value={years} onChange={(e)=>setYears(Number(e.target.value))}>{[1,2,3,4,5,6,7,8,9,10].map((n)=> (<option key={n} value={n}>{n}</option>))}</select></span></div>}>
              <div className="h-56 w-full">
                <ResponsiveContainer>
                  <LineChart data={yearData} margin={{ top: 10, right: 24, bottom: 10, left: 8 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#262626" />
                    <XAxis dataKey="year" stroke="#9ca3af" tick={{ fontSize: 12 }} tickMargin={8} />
                    <YAxis width={112} stroke="#9ca3af" tick={{ fontSize: 12 }} tickFormatter={(v)=>fmt(v)} />
                    <Tooltip formatter={(v)=>`${fmt(Math.round(v as number))} ฿`} />
                    <Legend />
                    <Line type="monotone" dataKey="base" name={lang==="th"?"ฐาน":"Base"} stroke="#60a5fa" strokeWidth={2} dot />
                    <Line type="monotone" dataKey="best" name={lang==="th"?"ดีที่สุด":"Best"} stroke="#34d399" strokeWidth={2} dot />
                    <Line type="monotone" dataKey="worst" name={lang==="th"?"แย่สุด":"Worst"} stroke="#f43f5e" strokeWidth={2} dot />
                  </LineChart>
                </ResponsiveContainer>
              </div>
              {/* Monthly snapshot table under yearly to match recipe height */}
              <div className="mt-4 rounded-2xl border border-neutral-800 overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead className="bg-neutral-900/60">
                    <tr className="text-neutral-400">
                      <th className="px-3 py-2 text-left">{lang==="th"?"เดือน":"Month"}</th>
                      <th className="px-3 py-2 text-right">{lang==="th"?"ฐาน":"Base"}</th>
                      <th className="px-3 py-2 text-right">{lang==="th"?"ดีที่สุด":"Best"}</th>
                      <th className="px-3 py-2 text-right">{lang==="th"?"แย่สุด":"Worst"}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {chartData.map((r) => (
                      <tr key={r.month} className="border-t border-neutral-800/70">
                        <td className="px-3 py-2 font-medium">{r.month}</td>
                        <td className="px-3 py-2 text-right whitespace-nowrap">{fmt(Math.round(r.base))} ฿</td>
                        <td className="px-3 py-2 text-right text-emerald-300 whitespace-nowrap">{fmt(Math.round(r.best))} ฿</td>
                        <td className="px-3 py-2 text-right text-rose-300 whitespace-nowrap">{fmt(Math.round(r.worst))} ฿</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Section>
          </div>
        </div>

        <div className="text-xs text-neutral-500 leading-relaxed mt-6">
          <p>Assumptions reflect PRD: single-dish Kapraw + optional fried egg and Coca-Cola. Seat capacity uses 60% dining area and ~1.2 sqm/guest guideline. ±10% accuracy tolerance as per PRD.</p>
        </div>
      </div>
    </div>
  );
}
